import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    ArrowRight,
    Building2,
    CalendarCheck,
    Check,
    MapPin,
    MessageCircle,
    Phone,
    Settings,
    ShieldCheck,
    Wrench,
    Zap,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { useQuote } from "@/components/QuoteModal";
import { IMG, LOCATIONS, TEL_URL, WA_URL } from "@/lib/site";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.95, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

const FadeUp = ({ children, delay, className = "", testid }) => (
    <motion.div
        data-testid={testid}
        initial={{ opacity: 0, y: 22 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay, ease: EASE }}
        className={className}
    >
        {children}
    </motion.div>
);

const HIGHLIGHTS = [
    {
        icon: Building2,
        title: "PREMIUM INSTALLATION",
        text: "Professional lift installation with a focus on safety, quality and reliable operation.",
    },
    {
        icon: Settings,
        title: "MULTI-BRAND MAINTENANCE",
        text: "Maintenance and servicing support for elevators of different brands, subject to equipment requirements.",
    },
    {
        icon: Zap,
        title: "BREAKDOWN RESPONSE",
        text: "Troubleshooting and repair support when your elevator stops working.",
    },
    {
        icon: ShieldCheck,
        title: "PROFESSIONAL SERVICE",
        text: "Focused on safety, reliability and long-term elevator performance.",
    },
];

const SERVICES = [
    {
        icon: Building2,
        title: "Lift Installation",
        text: "Professional elevator installation for residential and commercial properties — planned, installed and commissioned with care.",
        img: IMG.capsule,
        alt: "Panoramic glass capsule elevators on a modern high-rise atrium",
        testid: "service-card-installation",
    },
    {
        icon: CalendarCheck,
        title: "Maintenance & AMC",
        text: "Preventive maintenance and annual maintenance support that keeps your elevator safe, smooth and dependable.",
        img: IMG.caliper,
        alt: "Engineer performing precision technical inspection",
        testid: "service-card-amc",
    },
    {
        icon: Zap,
        title: "Breakdown Services",
        text: "Responsive troubleshooting and repair support to help restore elevator operation when it stops working.",
        img: IMG.machinery,
        alt: "Engineer calibrating machinery under inspection lighting",
        testid: "service-card-breakdown",
    },
    {
        icon: Wrench,
        title: "Repair & Troubleshooting",
        text: "Fault diagnosis, component inspection, repair and replacement support across elevator systems.",
        img: IMG.shaft,
        alt: "Vertical elevator shaft atrium showing structural engineering",
        testid: "service-card-repair",
    },
];

const WHY_POINTS = [
    "Professional installation",
    "Preventive maintenance",
    "Multi-brand service support",
    "Breakdown troubleshooting",
    "Safety-focused service",
    "Residential & commercial solutions",
    "Responsive customer support",
];

export default function Home() {
    const openQuote = useQuote();
    const { scrollY } = useScroll();
    const heroImgY = useTransform(scrollY, [0, 900], [0, 180]);
    const heroFade = useTransform(scrollY, [0, 550], [1, 0.3]);

    return (
        <>
            <Seo
                title="K1 ELEVATOR'S | Lift Installation & Elevator Maintenance — Patna, Gaya, Ranchi"
                description="K1 ELEVATOR'S is a premium elevator company in Patna offering lift installation, elevator maintenance, AMC and breakdown services across Patna, Gaya and Ranchi. Call +91 89694 49945."
            />

            {/* HERO */}
            <section className="relative flex min-h-[100svh] items-center overflow-hidden bg-[#0B1528]">
                <motion.div style={{ y: heroImgY }} className="absolute inset-0">
                    <motion.img
                        src={IMG.lobby}
                        alt="Modern premium building lobby with stainless steel elevator portals"
                        initial={{ scale: 1.18, opacity: 0.5 }}
                        animate={{ scale: 1.06, opacity: 1 }}
                        transition={{ duration: 2.4, ease: EASE }}
                        className="h-full w-full object-cover"
                    />
                </motion.div>
                <div className="absolute inset-0 bg-gradient-to-r from-[#0B1528]/95 via-[#0B1528]/80 to-[#0B1528]/45" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1528] via-transparent to-transparent" />

                <motion.div
                    style={{ opacity: heroFade }}
                    className="relative mx-auto w-full max-w-7xl px-5 pb-28 pt-36 sm:px-8"
                >
                    <FadeUp delay={0.15}>
                        <p className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.25em] text-[#93C5FD] backdrop-blur-md">
                            K1 Elevator&rsquo;s — Premium Elevator Services
                        </p>
                    </FadeUp>
                    <h1
                        data-testid="hero-headline"
                        className="mt-6 max-w-3xl text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl"
                    >
                        <MaskedLine delay={0.35}>ELEVATING SAFETY.</MaskedLine>
                        <MaskedLine delay={0.52}>
                            <span className="text-[#60A5FA]">ELEVATING COMFORT.</span>
                        </MaskedLine>
                    </h1>
                    <FadeUp delay={0.8}>
                        <p className="mt-5 text-lg font-semibold text-white sm:text-xl">
                            Premium Elevator Installation, Maintenance &amp; Breakdown Services
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.95}>
                        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">
                            K1 ELEVATOR&rsquo;S provides professional elevator solutions for
                            residential, commercial and other properties — from new lift
                            installation and preventive maintenance to breakdown and repair
                            services.
                        </p>
                    </FadeUp>
                    <FadeUp delay={1.1}>
                        <div className="mt-9 flex flex-wrap items-center gap-4">
                            <motion.button
                                type="button"
                                onClick={openQuote}
                                whileHover={{ scale: 1.04 }}
                                whileTap={{ scale: 0.95 }}
                                data-testid="hero-quote-btn"
                                className="inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-7 py-3.5 text-sm font-bold text-white shadow-[0_4px_20px_rgba(37,99,235,0.45)] transition-[background-color,box-shadow] duration-200 hover:bg-[#1D4ED8] hover:shadow-[0_8px_28px_rgba(37,99,235,0.55)]"
                            >
                                GET A FREE QUOTE <ArrowRight className="h-4 w-4" />
                            </motion.button>
                            <motion.a
                                href={TEL_URL}
                                whileHover={{ scale: 1.04 }}
                                whileTap={{ scale: 0.95 }}
                                data-testid="hero-call-btn"
                                className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-7 py-3.5 text-sm font-bold text-white backdrop-blur-md transition-[background-color,border-color] duration-200 hover:border-white/60 hover:bg-white/20"
                            >
                                <Phone className="h-4 w-4" /> CALL NOW
                            </motion.a>
                        </div>
                    </FadeUp>
                    <FadeUp delay={1.25}>
                        <p className="mt-10 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                            <MapPin className="h-3.5 w-3.5 text-[#60A5FA]" />
                            Serving Patna &bull; Gaya &bull; Ranchi
                        </p>
                    </FadeUp>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.8, duration: 1 }}
                    className="absolute bottom-7 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 sm:flex"
                >
                    <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-500">
                        Scroll
                    </span>
                    <div className="h-10 w-px overflow-hidden bg-white/15">
                        <motion.div
                            className="h-4 w-px bg-[#60A5FA]"
                            animate={{ y: [0, 40] }}
                            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                        />
                    </div>
                </motion.div>
            </section>

            {/* TRUST HIGHLIGHTS */}
            <section className="relative z-10 mx-auto -mt-16 max-w-7xl px-5 sm:px-8">
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                    {HIGHLIGHTS.map((h, i) => (
                        <Reveal
                            key={h.title}
                            delay={i * 90}
                            className="group rounded-2xl border border-slate-200/90 bg-white p-6 shadow-[0_12px_36px_rgb(15,23,42,0.08)] transition-[box-shadow,border-color,transform] duration-300 hover:-translate-y-1 hover:border-blue-300 hover:shadow-[0_16px_40px_rgb(37,99,235,0.14)]"
                        >
                            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F0F5FD] text-[#1D4ED8] transition-colors duration-300 group-hover:bg-[#1D4ED8] group-hover:text-white">
                                <h.icon className="h-6 w-6" />
                            </div>
                            <h2 className="mt-5 text-sm font-extrabold tracking-wide text-[#0B1528]">
                                {h.title}
                            </h2>
                            <p className="mt-2 text-sm leading-relaxed text-slate-500">{h.text}</p>
                        </Reveal>
                    ))}
                </div>
            </section>

            <div className="mt-20">
                <Marquee />
            </div>

            {/* SERVICES PREVIEW */}
            <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-28">
                <Reveal className="max-w-2xl">
                    <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#2563EB]">
                        What We Do
                    </p>
                    <h2 className="mt-4 text-3xl font-bold tracking-tight text-[#0B1528] sm:text-4xl">
                        COMPLETE ELEVATOR SOLUTIONS
                    </h2>
                    <p className="mt-4 text-base leading-relaxed text-slate-600">
                        From installation to ongoing maintenance, K1 ELEVATOR&rsquo;S helps keep
                        your elevator safe, reliable and operational.
                    </p>
                </Reveal>

                <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {SERVICES.map((s, i) => (
                        <Reveal
                            key={s.title}
                            delay={i * 90}
                            className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_4px_20px_rgb(15,23,42,0.04)] transition-[box-shadow,border-color,transform] duration-300 hover:-translate-y-1.5 hover:border-blue-300 hover:shadow-[0_16px_40px_rgb(37,99,235,0.14)]"
                        >
                            <div className="relative h-44 overflow-hidden">
                                <img
                                    src={s.img}
                                    alt={s.alt}
                                    loading="lazy"
                                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1528]/50 to-transparent" />
                                <div className="absolute bottom-3 left-3 flex h-10 w-10 items-center justify-center rounded-lg bg-white/95 text-[#1D4ED8] shadow-md">
                                    <s.icon className="h-5 w-5" />
                                </div>
                            </div>
                            <div className="flex flex-1 flex-col p-5">
                                <h3 className="text-lg font-bold text-[#0B1528]">{s.title}</h3>
                                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-500">
                                    {s.text}
                                </p>
                                <Link
                                    to="/services"
                                    data-testid={s.testid}
                                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#1D4ED8] transition-colors duration-200 hover:text-[#1E40AF]"
                                >
                                    Learn More <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                                </Link>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* WHY K1 */}
            <section className="bg-[#F0F5FD] py-24 sm:py-28">
                <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
                    <Reveal className="relative">
                        <div className="overflow-hidden rounded-3xl shadow-[0_24px_60px_rgb(11,21,40,0.18)]">
                            <img
                                src={IMG.cabin}
                                alt="Premium elevator cabin interior with elegant finishes"
                                loading="lazy"
                                className="h-full w-full object-cover"
                            />
                        </div>
                        <div className="absolute -bottom-6 -right-4 hidden rounded-2xl bg-[#0B1528] px-6 py-5 text-white shadow-xl sm:block">
                            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#60A5FA]">
                                Our Focus
                            </p>
                            <p className="mt-1 text-lg font-extrabold">
                                Safety. Reliability. Performance.
                            </p>
                        </div>
                    </Reveal>

                    <div>
                        <Reveal>
                            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#2563EB]">
                                Why K1 Elevator&rsquo;s
                            </p>
                            <h2 className="mt-4 text-3xl font-bold tracking-tight text-[#0B1528] sm:text-4xl">
                                RELIABILITY THAT MOVES WITH YOU
                            </h2>
                        </Reveal>
                        <ul className="mt-8 grid gap-4 sm:grid-cols-2">
                            {WHY_POINTS.map((point, i) => (
                                <Reveal key={point} delay={i * 60} className="flex items-center gap-3">
                                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#1D4ED8] text-white">
                                        <Check className="h-4 w-4" />
                                    </span>
                                    <span className="text-sm font-semibold text-slate-700">{point}</span>
                                </Reveal>
                            ))}
                        </ul>
                        <Reveal delay={200}>
                            <Link
                                to="/about"
                                className="mt-9 inline-flex items-center gap-2 text-sm font-bold text-[#1D4ED8] transition-colors duration-200 hover:text-[#1E40AF]"
                            >
                                More about K1 Elevator&rsquo;s <ArrowRight className="h-4 w-4" />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* LOCATIONS */}
            <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-28">
                <Reveal className="mx-auto max-w-2xl text-center">
                    <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#2563EB]">
                        Where We Work
                    </p>
                    <h2 className="mt-4 text-3xl font-bold tracking-tight text-[#0B1528] sm:text-4xl">
                        OUR SERVICE LOCATIONS
                    </h2>
                </Reveal>
                <div className="mt-12 grid gap-6 md:grid-cols-3">
                    {LOCATIONS.map((loc, i) => (
                        <Reveal
                            key={loc.city}
                            delay={i * 100}
                            className="group rounded-2xl border border-slate-200/90 bg-white p-8 text-center shadow-[0_4px_20px_rgb(15,23,42,0.04)] transition-[box-shadow,border-color,transform] duration-300 hover:-translate-y-1 hover:border-blue-300 hover:shadow-[0_16px_40px_rgb(37,99,235,0.12)]"
                        >
                            <div
                                data-testid={loc.testid}
                                className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F0F5FD] text-[#1D4ED8] transition-colors duration-300 group-hover:bg-[#1D4ED8] group-hover:text-white"
                            >
                                <MapPin className="h-7 w-7" />
                            </div>
                            <h3 className="mt-5 text-xl font-extrabold tracking-wide text-[#0B1528]">
                                {loc.city}
                            </h3>
                            <p className="mt-2 text-sm leading-relaxed text-slate-500">{loc.short}</p>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="relative overflow-hidden bg-[#0B1528] py-24">
                <div
                    className="absolute inset-0 opacity-[0.15]"
                    style={{
                        backgroundImage:
                            "linear-gradient(rgba(96,165,250,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(96,165,250,0.5) 1px, transparent 1px)",
                        backgroundSize: "44px 44px",
                    }}
                />
                <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
                    <Reveal>
                        <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                            NEED ELEVATOR SERVICE?
                        </h2>
                        <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-slate-300">
                            Whether you need a new lift, regular maintenance or breakdown
                            assistance, talk to K1 ELEVATOR&rsquo;S.
                        </p>
                        <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
                            <motion.button
                                type="button"
                                onClick={openQuote}
                                whileHover={{ scale: 1.04 }}
                                whileTap={{ scale: 0.95 }}
                                data-testid="home-cta-quote-btn"
                                className="inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-7 py-3.5 text-sm font-bold text-white shadow-[0_4px_20px_rgba(37,99,235,0.45)] transition-colors duration-200 hover:bg-[#1D4ED8]"
                            >
                                GET A FREE QUOTE <ArrowRight className="h-4 w-4" />
                            </motion.button>
                            <a
                                href={WA_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="home-cta-whatsapp-btn"
                                className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-7 py-3.5 text-sm font-bold text-white backdrop-blur-md transition-[background-color,border-color] duration-200 hover:border-white/60 hover:bg-white/20"
                            >
                                <MessageCircle className="h-4 w-4" /> WHATSAPP US
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
